import { useEffect, useMemo, useState } from 'react';

import { getDaysInMonth } from 'date-fns';
import { Control, useController } from 'react-hook-form';

import { useTranslate } from '@i18n';
import { Box, Select, Stack, Text, u } from '@ui';

import { UserWelcomeWizardForm } from '../useUserWelcomeWizard';

type AgeStepProps = {
    control: Control<UserWelcomeWizardForm>;
};

type DateParts = {
    month: string;
    day: string;
    year: string;
};

const MIN_YEAR = 1900;
const MAX_YEAR = new Date().getFullYear();
const LEAP_YEAR = 2000;

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => {
    const value = String(index + 1);

    return {
        value,
        label: value,
    };
});

const YEAR_OPTIONS = Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, index) => {
    const value = String(MAX_YEAR - index);

    return {
        value,
        label: value,
    };
});

const getInitialParts = (value: Date | null | undefined): DateParts => {
    if (!value || Number.isNaN(value.getTime())) {
        return {
            month: '',
            day: '',
            year: '',
        };
    }

    return {
        month: String(value.getMonth() + 1),
        day: String(value.getDate()),
        year: String(value.getFullYear()),
    };
};

const getMaxDayForMonth = (month: string, year: string) =>
    getDaysInMonth(new Date(Number(year) || LEAP_YEAR, Number(month) - 1));

const normalizeDay = (day: string, month: string, year: string) =>
    Number(day) <= getMaxDayForMonth(month, year) ? day : '';

const createDate = ({ month, day, year }: DateParts) => {
    if (!month || !day || !year) {
        return null;
    }

    return new Date(Number(year), Number(month) - 1, Number(day));
};

export const AgeStep = ({ control }: AgeStepProps) => {
    const t = useTranslate();

    const { field, fieldState } = useController({
        control,
        name: 'dateOfBirth',
    });

    const initialParts = useMemo(() => getInitialParts(field.value ?? null), [field.value]);

    const [parts, setParts] = useState<DateParts>(initialParts);

    useEffect(() => {
        setParts(initialParts);
    }, [initialParts]);

    const dayOptions = Array.from({ length: getMaxDayForMonth(parts.month, parts.year) }, (_, index) => {
        const value = String(index + 1);

        return {
            value,
            label: value,
        };
    });

    const updateParts = (changes: Partial<DateParts>) => {
        const nextParts = {
            ...parts,
            ...changes,
        };

        nextParts.day = normalizeDay(nextParts.day, nextParts.month, nextParts.year);

        setParts(nextParts);

        field.onChange(createDate(nextParts));
    };

    return (
        <Stack width="100%" alignItems="center" gap={u(1)}>
            <Box
                display="flex"
                width="100%"
                flexDirection={{ xs: 'column', md: 'row' }}
                justifyContent="center"
                alignItems="center"
                gap={u(1)}
            >
                <Stack>
                    <Text variant="h4">{t('user_settings.date_of_birth_month_label')}</Text>

                    <Select
                        options={MONTH_OPTIONS}
                        selectedValue={parts.month || undefined}
                        onChange={([month = '']) => updateParts({ month })}
                        label={t('user_settings.date_of_birth_month_label')}
                        placeholder={t('user_settings.date_of_birth_month_placeholder')}
                    />
                </Stack>

                <Stack>
                    <Text variant="h4">{t('user_settings.date_of_birth_day_label')}</Text>

                    <Select
                        options={dayOptions}
                        selectedValue={parts.day || undefined}
                        onChange={([day = '']) => updateParts({ day })}
                        label={t('user_settings.date_of_birth_day_label')}
                        placeholder={t('user_settings.date_of_birth_day_placeholder')}
                    />
                </Stack>

                <Stack>
                    <Text variant="h4">{t('user_settings.date_of_birth_year_label')}</Text>

                    <Select
                        options={YEAR_OPTIONS}
                        selectedValue={parts.year || undefined}
                        onChange={([year = '']) => updateParts({ year })}
                        label={t('user_settings.date_of_birth_year_label')}
                        placeholder={t('user_settings.date_of_birth_year_placeholder')}
                    />
                </Stack>
            </Box>

            {fieldState.error?.message ? (
                <Text variant="small" color="danger">
                    {fieldState.error.message}
                </Text>
            ) : null}
        </Stack>
    );
};
